import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Star,
  Award,
  Users,
  Linkedin,
  Globe,
  Loader2,
  Video,
  Presentation,
  Radio,
  Play,
} from 'lucide-react';
import { speakersAPI, webinarsAPI, seminarsAPI } from '@/services/api';
import { WebinarCard } from '@/components/common/WebinarCard';
import { Breadcrumb } from '@/components/common/Breadcrumb';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to resolve image URLs (handles both absolute and relative paths)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

// Professional corporate avatars for speakers (AI-generated fallback)
const CORPORATE_AVATARS = [
  'https://static.prod-images.emergentagent.com/jobs/da1e0244-c214-4080-85d4-75157f8dce2d/images/e40628c3f3d8e1af5cd4d565858a1d353afa6113d36e9200cc6afaef29725122.png',
  'https://static.prod-images.emergentagent.com/jobs/da1e0244-c214-4080-85d4-75157f8dce2d/images/5512ed49b882fb5ca0eba4673a522170b96ca8108b16c1e60fad2ba090f64ce0.png',
  'https://static.prod-images.emergentagent.com/jobs/da1e0244-c214-4080-85d4-75157f8dce2d/images/f97146d077f487f65f28a7670be3f7d2450091d99db8002401d48a14ea47718e.png',
  'https://static.prod-images.emergentagent.com/jobs/da1e0244-c214-4080-85d4-75157f8dce2d/images/3844e4a7412759c72630e7779c889dd437fa68b0faae2e9fd18d383f8861b02b.png',
];

// Get consistent corporate avatar based on speaker name
const getCorporateAvatar = (firstName, lastName) => {
  const hash = (firstName || '').charCodeAt(0) + (lastName || '').charCodeAt(0);
  return CORPORATE_AVATARS[hash % CORPORATE_AVATARS.length];
};

// Corporate 3D avatar for inactive speakers (default)
const INACTIVE_AVATAR = 'https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=200&h=200&fit=crop';

export default function SpeakerDetailPage() {
  const { id } = useParams();
  const [speaker, setSpeaker] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [seminars, setSeminars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('webinars');
  const [webinarFilter, setWebinarFilter] = useState('all');
  const [seminarFilter, setSeminarFilter] = useState('all');

  useEffect(() => {
    fetchSpeakerData();
  }, [id]);

  const fetchSpeakerData = async () => {
    try {
      // Resolve speaker first (endpoint accepts slug OR id)
      const speakerRes = await speakersAPI.getById(id);
      const speakerData = speakerRes.data;
      setSpeaker(speakerData);

      // Always fetch linked content by the resolved speaker UUID (speaker_ids are UUIDs, not slugs)
      const resolvedId = speakerData?.id || id;
      const [webinarsRes, seminarsRes] = await Promise.all([
        webinarsAPI.getAll({ speaker_id: resolvedId, per_page: 100 }),
        seminarsAPI.getAll({ speaker_id: resolvedId, per_page: 100 }),
      ]);

      const webinarsData = webinarsRes.data?.items || webinarsRes.data || [];
      const seminarsData = seminarsRes.data?.items || seminarsRes.data || [];

      setWebinars(Array.isArray(webinarsData) ? webinarsData : []);
      setSeminars(Array.isArray(seminarsData) ? seminarsData : []);
    } catch (error) {
      console.error('Error fetching speaker:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter webinars based on selected filter
  const filteredWebinars = webinars.filter(w => {
    if (webinarFilter === 'all') return true;
    if (webinarFilter === 'live') return w.webinar_type === 'live';
    if (webinarFilter === 'recorded') return w.webinar_type === 'on_demand' || w.webinar_type === 'recorded';
    if (webinarFilter === 'certified') return w.cpe_credits > 0 || w.has_cpe_credits;
    return true;
  });

  // Filter seminars based on selected filter
  const filteredSeminars = seminars.filter(s => {
    if (seminarFilter === 'all') return true;
    if (seminarFilter === 'live') return s.seminar_type === 'live';
    if (seminarFilter === 'recorded') return s.seminar_type === 'on_demand' || s.seminar_type === 'recorded';
    if (seminarFilter === 'certified') return s.cpe_credits > 0 || s.has_cpe_credits;
    return true;
  });

  // Count by type for filter badges
  const webinarCounts = {
    all: webinars.length,
    live: webinars.filter(w => w.webinar_type === 'live').length,
    recorded: webinars.filter(w => w.webinar_type === 'on_demand' || w.webinar_type === 'recorded').length,
    certified: webinars.filter(w => w.cpe_credits > 0 || w.has_cpe_credits).length,
  };

  const seminarCounts = {
    all: seminars.length,
    live: seminars.filter(s => s.seminar_type === 'live').length,
    recorded: seminars.filter(s => s.seminar_type === 'on_demand' || s.seminar_type === 'recorded').length,
    certified: seminars.filter(s => s.cpe_credits > 0 || s.has_cpe_credits).length,
  };

  // Format bio - limit to 2-3 paragraphs max
  const formatBioForHero = (bio) => {
    if (!bio) return null;
    // Split by double newlines or periods followed by space and capital letter
    const sentences = bio.split(/(?<=\.)\s+/).filter(s => s.trim());
    
    // Group sentences into paragraphs (approximately 2-3 sentences per paragraph)
    const paragraphs = [];
    let currentPara = [];
    
    sentences.forEach((sentence, idx) => {
      currentPara.push(sentence.trim());
      // Create new paragraph every 2-3 sentences or at natural breaks
      if (currentPara.length >= 3 || sentence.includes('\n\n')) {
        paragraphs.push(currentPara.join(' '));
        currentPara = [];
      }
    });
    
    // Add remaining sentences
    if (currentPara.length > 0) {
      paragraphs.push(currentPara.join(' '));
    }
    
    // Return only first 2-3 paragraphs
    return paragraphs.slice(0, 3).map((p, idx) => (
      <p key={idx} className="mb-3 last:mb-0 text-zinc-600 leading-relaxed">
        {p}
      </p>
    ));
  };

  const isActive = speaker?.is_active !== false;
  const profileImage = isActive 
    ? (resolveImageUrl(speaker?.profile_image) || getCorporateAvatar(speaker?.first_name, speaker?.last_name))
    : getCorporateAvatar(speaker?.first_name, speaker?.last_name);

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
      </div>
    );
  }

  if (!speaker) {
    return (
      <div className="min-h-screen bg-white pt-24">
        <div className="container-custom py-20 text-center">
          <h1 className="font-heading text-2xl text-navy-700 mb-4">Speaker not found</h1>
          <Link to="/">
            <Button variant="outline" className="rounded-xl">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent" data-testid="speaker-detail-page">
      {/* Speaker Hero — MailCraft light grid canvas */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-12 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <Breadcrumb lastLabel={`${speaker.first_name} ${speaker.last_name}`} />
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Side - Image and Stats */}
            <div className="flex flex-col items-center lg:items-start gap-6 lg:w-64 flex-shrink-0">
              {/* Profile Image */}
              <div className="relative">
                <div className={`w-48 h-48 rounded-3xl overflow-hidden shadow-[0_16px_48px_-20px_rgba(15,23,42,0.35)] border border-zinc-200 ${!isActive ? 'grayscale' : ''}`}>
                  <img
                    src={profileImage}
                    alt={`${speaker.first_name} ${speaker.last_name}`}
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.src = INACTIVE_AVATAR}
                  />
                </div>
                {speaker.is_featured && isActive && (
                  <div className="absolute -bottom-3 -right-3 w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                )}
                {!isActive && (
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-zinc-500 rounded-full">
                    <span className="text-xs text-white font-medium">Archived</span>
                  </div>
                )}
              </div>

              {/* Expert Speaker badge (below image) */}
              <span className="mono-label inline-flex items-center gap-2 text-[11px] text-zinc-600 bg-white border border-zinc-200 rounded-full px-3 py-1">
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                {isActive ? 'Expert Speaker' : 'Archived Speaker'}
              </span>

              {/* Stats */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl px-3 py-2">
                  <Award className="w-4 h-4 text-emerald-600" />
                  <span className="text-zinc-700 text-sm font-medium">{speaker.years_experience || 10}+ Years</span>
                </div>
                <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl px-3 py-2">
                  <Video className="w-4 h-4 text-emerald-600" />
                  <span className="text-zinc-700 text-sm font-medium">{webinars.length} Webinars</span>
                </div>
                <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl px-3 py-2">
                  <Presentation className="w-4 h-4 text-emerald-600" />
                  <span className="text-zinc-700 text-sm font-medium">{seminars.length} Seminars</span>
                </div>
              </div>

              {/* Social Links */}
              {(speaker.linkedin_url || speaker.website_url) && (
                <div className="flex gap-3">
                  {speaker.linkedin_url && (
                    <a
                      href={speaker.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center text-zinc-700 hover:bg-zinc-900 hover:text-white transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                  {speaker.website_url && (
                    <a
                      href={speaker.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center text-zinc-700 hover:bg-zinc-900 hover:text-white transition-colors"
                    >
                      <Globe className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Right Side - Name, Expertise, Bio */}
            <div className="flex-1 text-center lg:text-left">
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-[-0.03em] text-zinc-950 mb-2">
                {speaker.first_name} {speaker.last_name}
              </h1>
              <p className="font-medium text-lg mb-4 text-emerald-600">
                {speaker.expertise_areas?.join(' • ') || 'Industry Expert'}
              </p>

              {/* Bio - Within container width, 2-3 paragraphs max */}
              <div className="max-w-3xl">
                {speaker.bio ? formatBioForHero(speaker.bio) : (
                  <p className="text-zinc-600 leading-relaxed">
                    {speaker.first_name} {speaker.last_name} is a distinguished expert in {speaker.expertise_areas?.join(', ') || 'compliance and governance'}. 
                    With over {speaker.years_experience || 10} years of experience, they have helped countless organizations navigate complex regulatory requirements.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters and Content Section */}
      <section className="py-8">
        <div className="container-custom">
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white border border-slate-200 p-1 rounded-xl mb-6 w-full max-w-md grid grid-cols-2">
              <TabsTrigger 
                value="webinars" 
                className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg"
              >
                <Video className="w-4 h-4 mr-2" />
                Webinars ({webinars.length})
              </TabsTrigger>
              <TabsTrigger 
                value="seminars" 
                className="data-[state=active]:bg-zinc-900 data-[state=active]:text-white rounded-lg"
              >
                <Presentation className="w-4 h-4 mr-2" />
                Seminars ({seminars.length})
              </TabsTrigger>
            </TabsList>

            {/* Webinars Tab */}
            <TabsContent value="webinars" className="mt-0">
              {/* Webinar Filters */}
              <div className="flex flex-wrap gap-2 mb-6 bg-white p-4 rounded-xl border border-slate-200">
                <Button
                  variant={webinarFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setWebinarFilter('all')}
                  className={webinarFilter === 'all' ? 'bg-zinc-900 text-white' : ''}
                >
                  All ({webinarCounts.all})
                </Button>
                <Button
                  variant={webinarFilter === 'live' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setWebinarFilter('live')}
                  className={webinarFilter === 'live' ? 'bg-red-500 text-white' : ''}
                >
                  <Radio className="w-3 h-3 mr-1" />
                  Live ({webinarCounts.live})
                </Button>
                <Button
                  variant={webinarFilter === 'recorded' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setWebinarFilter('recorded')}
                  className={webinarFilter === 'recorded' ? 'bg-blue-500 text-white' : ''}
                >
                  <Play className="w-3 h-3 mr-1" />
                  Recorded ({webinarCounts.recorded})
                </Button>
                <Button
                  variant={webinarFilter === 'certified' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setWebinarFilter('certified')}
                  className={webinarFilter === 'certified' ? 'bg-green-500 text-white' : ''}
                >
                  <Award className="w-3 h-3 mr-1" />
                  Certified ({webinarCounts.certified})
                </Button>
              </div>

              {/* Webinar Cards - 3-column grid */}
              {filteredWebinars.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredWebinars.map((webinar, idx) => (
                    <WebinarCard
                      key={webinar.id}
                      item={webinar}
                      type="webinar"
                      animationDelay={idx * 0.03}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                  <Video className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-navy-700 mb-2">
                    No webinars found
                  </h3>
                  <p className="text-slate-500">
                    {webinarFilter !== 'all' 
                      ? 'Try a different filter to see more webinars.' 
                      : `${speaker.first_name} hasn't conducted any webinars yet.`}
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Seminars Tab */}
            <TabsContent value="seminars" className="mt-0">
              {/* Seminar Filters */}
              <div className="flex flex-wrap gap-2 mb-6 bg-white p-4 rounded-xl border border-slate-200">
                <Button
                  variant={seminarFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSeminarFilter('all')}
                  className={seminarFilter === 'all' ? 'bg-zinc-900 text-white' : ''}
                >
                  All ({seminarCounts.all})
                </Button>
                <Button
                  variant={seminarFilter === 'live' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSeminarFilter('live')}
                  className={seminarFilter === 'live' ? 'bg-red-500 text-white' : ''}
                >
                  <Radio className="w-3 h-3 mr-1" />
                  Live ({seminarCounts.live})
                </Button>
                <Button
                  variant={seminarFilter === 'recorded' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSeminarFilter('recorded')}
                  className={seminarFilter === 'recorded' ? 'bg-blue-500 text-white' : ''}
                >
                  <Play className="w-3 h-3 mr-1" />
                  Recorded ({seminarCounts.recorded})
                </Button>
                <Button
                  variant={seminarFilter === 'certified' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSeminarFilter('certified')}
                  className={seminarFilter === 'certified' ? 'bg-green-500 text-white' : ''}
                >
                  <Award className="w-3 h-3 mr-1" />
                  Certified ({seminarCounts.certified})
                </Button>
              </div>

              {/* Seminar Cards - 3-column grid */}
              {filteredSeminars.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredSeminars.map((seminar, idx) => (
                    <WebinarCard
                      key={seminar.id}
                      item={seminar}
                      type="seminar"
                      animationDelay={idx * 0.03}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                  <Presentation className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-navy-700 mb-2">
                    No seminars found
                  </h3>
                  <p className="text-slate-500">
                    {seminarFilter !== 'all' 
                      ? 'Try a different filter to see more seminars.' 
                      : `${speaker.first_name} hasn't conducted any seminars yet.`}
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
